import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// PPC Machine Calculator — Standard + Actual modes. Computes machines-required
// PER MACHINE TYPE (via the ppc_op_machine_map inventory mapping) and compares
// against machines_master inventory. Standalone; does not touch smart-loading.

const C = { ink: '#3B3A8F', ink2: '#2D2C72', red: '#E22D2D', amber: '#9A6B00', green: '#166534', line: '#E8E7E7', soft: '#F7F6F3' };
const box = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: 16 };
const lbl = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };
const inp = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const btn = { padding: '8px 16px', background: C.ink, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const fmt = (n, d = 0) => n == null || isNaN(n) ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: d });

const SAM_SRC = {
  standard: { label: 'Standard', c: C.ink, bg: '#EEEDF8' },
  measured: { label: 'Measured', c: C.green, bg: '#EAF7EE' },
  pb: { label: 'Bulletin', c: C.amber, bg: '#FFF8E6' },
  manual: { label: 'Manual', c: '#7c3aed', bg: '#F3EEFD' },
  none: { label: 'No SAM', c: C.red, bg: '#FCEBEB' },
};

export default function MachineCalculator() {
  const [gpos, setGpos] = useState([]);
  const [orderId, setOrderId] = useState('');
  const [mode, setMode] = useState('standard');          // standard | actual
  const [calcMode, setCalcMode] = useState('A');          // A=deadline→machines, B=machines→time
  const [params, setParams] = useState({ target_qty: '', efficiency: 60, working_mins: 510, days: 30 });
  const [manual, setManual] = useState({});               // process_code → sam
  const [machines, setMachines] = useState({});           // mm_code → allocated (mode B)
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Stage 2 — factory-aware capacity
  const [fac, setFac] = useState(null);            // /capacity/calculate result
  const [facBusy, setFacBusy] = useState(false);
  const [commitBusy, setCommitBusy] = useState(false);
  const [reservations, setReservations] = useState({ data: [], summary: [] });
  const [showCommits, setShowCommits] = useState(false);

  useEffect(() => { api.get('/machine-calc/gpos').then(r => setGpos(r.data.data || [])).catch(() => {}); }, []);

  const selected = gpos.find(g => g.id === orderId);
  useEffect(() => {                                       // default target qty to GPO qty
    if (selected && !params.target_qty) setParams(p => ({ ...p, target_qty: selected.quantity || '' }));
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculate = useCallback(async (overrides = {}) => {
    if (!orderId) { setErr('Pick a GPO first'); return; }
    setBusy(true); setErr('');
    try {
      const body = {
        order_id: orderId, mode, calc_mode: calcMode,
        target_qty: params.target_qty || (selected?.quantity || 0),
        efficiency: params.efficiency, working_mins: params.working_mins, days: params.days,
        manual_sams: manual, machines, ...overrides,
      };
      const r = await api.post('/machine-calc/calculate', body);
      setResult(r.data.data);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  }, [orderId, mode, calcMode, params, manual, machines, selected]);

  // Record a manual / confirmed SAM into the standard library (self-tuning loop).
  const recordSam = async (op, sam) => {
    try {
      const r = await api.post('/machine-calc/record-sam', {
        process_code: op.process_code, process_name: op.operation_name,
        machine_code: op.machine_type, division: op.division, actual_sam: sam,
      });
      alert(`Standard updated: ${op.operation_name} → ${Number(r.data.data.standard_sam).toFixed(3)} min (based on ${r.data.data.observation_count} observation${r.data.data.observation_count > 1 ? 's' : ''}).`);
      calculate();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  // Shared body for the factory-aware endpoints (same GPO, mode, SAMs as Stage 1).
  const facBody = () => ({ order_id: orderId, mode, target_qty: params.target_qty || (selected?.quantity || 0), efficiency: params.efficiency, working_mins: params.working_mins, manual_sams: manual });

  const checkCapacity = async () => {
    if (!orderId) { setErr('Pick a GPO first'); return; }
    setFacBusy(true); setErr('');
    try { const r = await api.post('/capacity/calculate', facBody()); setFac(r.data.data); }
    catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setFacBusy(false); }
  };
  const loadReservations = useCallback(async () => {
    try { const r = await api.get('/capacity/reservations?status=active'); setReservations({ data: r.data.data || [], summary: r.data.summary || [] }); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { loadReservations(); }, [loadReservations]);

  const commitPlan = async () => {
    if (!fac?.window?.has_window) { alert('This GPO has no T&A sewing window (M18/M19) — cannot reserve.'); return; }
    const shorts = (fac.types || []).filter(t => t.shortfall > 0);
    const msg = `Reserve this machine plan for GPO ${fac.gpo_no} over ${fac.window.start_date} → ${fac.window.end_date}?` +
      (shorts.length ? `\n\n⚠ ${shorts.length} type(s) short — you can still reserve the in-house portion and decide Buy/Jobwork for the rest.` : '');
    if (!window.confirm(msg)) return;
    setCommitBusy(true);
    try {
      const r = await api.post('/capacity/commit', facBody());
      alert(`Reserved ${r.data.data.count} machine type(s) for ${fac.gpo_no}.`);
      await checkCapacity(); await loadReservations();
    } catch (e) { alert('Commit failed: ' + (e.response?.data?.error || e.message)); }
    finally { setCommitBusy(false); }
  };
  const releasePlan = async (gpo) => {
    if (!window.confirm(`Release all capacity reserved by GPO ${gpo}?`)) return;
    try { await api.post('/capacity/release', { gpo_no: gpo }); await loadReservations(); if (fac) await checkCapacity(); }
    catch (e) { alert('Release failed: ' + (e.response?.data?.error || e.message)); }
  };

  const totals = result?.totals;
  const setP = (k, v) => setParams(p => ({ ...p, [k]: v }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 17, color: '#1a1a2e' }}>🧮 Machine Calculator</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>
          Machines required <strong>per machine type</strong> vs inventory. <strong>Standard</strong> uses the self-tuning SAM library; <strong>Actual</strong> uses the GPO's measured T&M study.
        </p>
      </div>

      {/* Controls */}
      <div style={{ ...box, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, alignItems: 'end' }}>
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Mode toggle */}
          <div style={{ display: 'inline-flex', border: `1px solid ${C.ink}`, borderRadius: 6, overflow: 'hidden' }}>
            {['standard', 'actual'].map(m => (
              <button key={m} onClick={() => { setMode(m); setResult(null); }}
                style={{ padding: '7px 16px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, textTransform: 'capitalize', background: mode === m ? C.ink : '#fff', color: mode === m ? '#fff' : C.ink }}>
                {m === 'standard' ? '📐 Standard' : '⏱️ Actual'}
              </button>
            ))}
          </div>
          {/* Calc-mode toggle */}
          <div style={{ display: 'inline-flex', border: '1px solid #bbb', borderRadius: 6, overflow: 'hidden' }}>
            {[['A', 'Deadline → Machines'], ['B', 'Machines → Time']].map(([k, l]) => (
              <button key={k} onClick={() => { setCalcMode(k); setResult(null); }}
                style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: calcMode === k ? '#444' : '#fff', color: calcMode === k ? '#fff' : '#444' }}>{l}</button>
            ))}
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl}>GPO / Order</label>
          <select style={inp} value={orderId} onChange={e => { setOrderId(e.target.value); setResult(null); setManual({}); setMachines({}); }}>
            <option value="">— Select a GPO —</option>
            {gpos.map(g => (
              <option key={g.id} value={g.id}>
                {(g.gpo_no || g.order_no)} · {g.buyer} · qty {fmt(g.quantity)}{g.has_study ? ' · 📊 study' : g.has_bulletin ? ' · 📋 bulletin' : ' · ⚠ no breakdown'}
              </option>
            ))}
          </select>
        </div>

        <div><label style={lbl}>Target Qty</label><input style={inp} type="number" value={params.target_qty} onChange={e => setP('target_qty', e.target.value)} placeholder={selected?.quantity || ''} /></div>
        <div><label style={lbl}>Efficiency %</label><input style={inp} type="number" value={params.efficiency} onChange={e => setP('efficiency', e.target.value)} /></div>
        <div><label style={lbl}>Working min/shift</label><input style={inp} type="number" value={params.working_mins} onChange={e => setP('working_mins', e.target.value)} /></div>
        {calcMode === 'A'
          ? <div><label style={lbl}>Deadline (days)</label><input style={inp} type="number" value={params.days} onChange={e => setP('days', e.target.value)} /></div>
          : <div style={{ fontSize: 10, color: '#888', alignSelf: 'center' }}>Set machines per type in the table, then Calculate.</div>}
        <div><button style={{ ...btn, width: '100%' }} disabled={busy || !orderId} onClick={() => calculate()}>{busy ? 'Calculating…' : 'Calculate'}</button></div>
      </div>

      {err && <div style={{ background: '#FCEBEB', color: C.red, padding: '8px 12px', borderRadius: 6, fontSize: 12 }}>{err}</div>}
      {result && !result.types?.length && <div style={{ ...box, color: '#888', fontSize: 13 }}>{result.message || 'No operation breakdown found for this GPO.'}</div>}

      {/* Summary cards */}
      {totals && result.types?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
          <Card title="Total SAM / garment" value={`${fmt(totals.total_sam, 3)} min`} />
          {calcMode === 'A' ? <>
            <Card title="Daily target" value={fmt(totals.daily_target)} sub={`over ${totals.days} days`} />
            <Card title="Total machines req." value={fmt(totals.total_machines_required)} accent={C.ink} />
            <Card title="Implied days @ that fleet" value={fmt(totals.implied_days)} sub={`bottleneck ${fmt(totals.implied_daily_output)}/day`} />
          </> : <>
            <Card title="Daily output" value={fmt(totals.daily_output)} sub={`bottleneck: ${totals.bottleneck || '—'}`} />
            <Card title="Days required" value={fmt(totals.days_required)} accent={C.ink} />
          </>}
          <Card title="Eff. min/machine/day" value={fmt(totals.eff_per_machine, 0)} sub={`${totals.efficiency}% × ${totals.working_mins}m`} />
          {(totals.missing_ops > 0 || totals.unmapped_ops > 0) &&
            <Card title="Flags" value={`${totals.missing_ops} no-SAM · ${totals.unmapped_ops} unmapped`} accent={C.red} />}
        </div>
      )}

      {/* Per-type table */}
      {result?.types?.length > 0 && (
        <div style={box}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 8 }}>PER MACHINE TYPE</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ background: C.soft, textAlign: 'left' }}>
              {['Machine Type', 'Ops', 'Type SAM (min)', calcMode === 'A' ? 'Required' : 'Machines', calcMode === 'A' ? 'Available' : 'Output/day', calcMode === 'A' ? 'Shortfall' : 'Available'].map(h =>
                <th key={h} style={{ padding: '8px 10px', fontSize: 11, color: '#555', borderBottom: `2px solid ${C.line}` }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {result.types.map((t, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f3f3', background: t.unmapped ? '#FFFBEB' : '#fff' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>
                    {t.machine_name}{t.machine_code && <span style={{ color: '#999', fontWeight: 400 }}> ({t.machine_code})</span>}
                    {t.unmapped && <span style={{ marginLeft: 6, fontSize: 10, color: C.amber, fontWeight: 700 }}>⚠ UNMAPPED</span>}
                  </td>
                  <td style={{ padding: '8px 10px' }}>{t.ops}</td>
                  <td style={{ padding: '8px 10px' }}>{fmt(t.type_sam, 3)}</td>
                  {calcMode === 'A' ? <>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: C.ink }}>{fmt(t.required)}</td>
                    <td style={{ padding: '8px 10px' }}>{t.unmapped ? '—' : fmt(t.available)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: t.shortfall > 0 ? C.red : C.green }}>
                      {t.unmapped ? '—' : t.shortfall > 0 ? `▲ ${fmt(t.shortfall)} short` : '✓ ok'}
                    </td>
                  </> : <>
                    <td style={{ padding: '8px 10px' }}>
                      {t.unmapped ? '—' : <input style={{ ...inp, width: 70, padding: '4px 6px' }} type="number"
                        value={machines[t.machine_code] ?? t.available} onChange={e => setMachines(m => ({ ...m, [t.machine_code]: e.target.value }))} />}
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: C.ink }}>{fmt(t.output)}</td>
                    <td style={{ padding: '8px 10px' }}>{t.unmapped ? '—' : fmt(t.available)}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
          {calcMode === 'B' && <div style={{ marginTop: 8, textAlign: 'right' }}><button style={btn} disabled={busy} onClick={() => calculate()}>Recalculate with machine counts</button></div>}
        </div>
      )}

      {/* ── Stage 2: Factory-aware capacity ───────────────────────────────── */}
      {result?.types?.length > 0 && (
        <div style={{ ...box, borderColor: C.ink }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.ink }}>🏭 FACTORY CAPACITY (this GPO's window vs other committed GPOs)</div>
            {/* Disabled ONLY while in flight. It greyed on !orderId too, with no tooltip, no
                colour change and no cursor change — the one button in the sweep that gave the
                operator nothing at all. checkCapacity already says 'Pick a GPO first'; it just
                could never run. */}
            <button style={{ ...btn, background: facBusy ? '#bbb' : '#444', cursor: facBusy ? 'not-allowed' : 'pointer' }}
              disabled={facBusy} onClick={checkCapacity}>{facBusy ? 'Checking…' : 'Check Factory Capacity'}</button>
          </div>

          {fac && !fac.window?.has_window && <div style={{ marginTop: 8, background: '#FFF8E6', color: C.amber, padding: '8px 12px', borderRadius: 6, fontSize: 12 }}>⚠ This GPO has no T&A sewing window (M18 line-in / M19 line-out). Capacity shown against full inventory; reservation is disabled until the window exists.</div>}

          {fac?.types?.length > 0 && (
            <>
              <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>
                Sewing window: <strong>{fac.window?.start_date || '—'} → {fac.window?.end_date || '—'}</strong>
                {fac.window?.working_days ? <span style={{ color: '#888' }}> · {fac.window.working_days} working days (Mon–Sat)</span> : null}
                <span style={{ color: '#888' }}> · source {fac.window?.source || '—'}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                <thead><tr style={{ background: C.soft, textAlign: 'left' }}>
                  {['Machine Type', 'Required', 'Total', 'Committed (other GPOs)', 'Under Maint.', 'Free (this window)', 'Shortfall', 'Options if short'].map(h =>
                    <th key={h} style={{ padding: '8px 10px', fontSize: 11, color: '#555', borderBottom: `2px solid ${C.line}` }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {fac.types.map((t, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f3f3', background: t.shortfall > 0 ? '#FCEBEB' : t.unmapped ? '#FFFBEB' : '#fff' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{t.machine_name}{t.machine_code && <span style={{ color: '#999', fontWeight: 400 }}> ({t.machine_code})</span>}{t.unmapped && <span style={{ marginLeft: 6, fontSize: 10, color: C.amber, fontWeight: 700 }}>⚠ UNMAPPED</span>}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: C.ink }}>{fmt(t.required)}</td>
                      <td style={{ padding: '8px 10px' }}>{t.unmapped ? '—' : fmt(t.total)}</td>
                      <td style={{ padding: '8px 10px', color: t.committed > 0 ? C.amber : '#888' }}>{t.unmapped ? '—' : fmt(t.committed)}</td>
                      <td style={{ padding: '8px 10px', color: t.under_maintenance > 0 ? C.red : '#888' }}>{t.unmapped ? '—' : (t.under_maintenance > 0 ? `🔧 ${fmt(t.under_maintenance)}` : '0')}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{t.unmapped ? '—' : fmt(t.free)}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: t.shortfall > 0 ? C.red : C.green }}>{t.unmapped ? '—' : t.shortfall > 0 ? `▲ ${fmt(t.shortfall)}` : '✓ ok'}</td>
                      <td style={{ padding: '8px 10px', fontSize: 11 }}>
                        {t.advisor
                          ? <span><strong style={{ color: C.ink }}>BUY</strong> {fmt(t.advisor.buy_machines)} {t.machine_code} <span style={{ color: '#999' }}>or</span> <strong style={{ color: '#7c3aed' }}>JOBWORK</strong> {fmt(t.advisor.jobwork_qty)} pcs</span>
                          : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {fac.totals && <div style={{ marginTop: 6, fontSize: 11, color: '#666' }}>Committed elsewhere (overlapping): <strong>{fmt(fac.totals.total_committed)}</strong> · Under maintenance (down/scheduled in window): <strong style={{ color: fac.totals.total_under_maintenance > 0 ? C.red : '#666' }}>{fmt(fac.totals.total_under_maintenance)}</strong> · Shortfall total: <strong style={{ color: fac.totals.total_shortfall > 0 ? C.red : C.green }}>{fmt(fac.totals.total_shortfall)}</strong> across {fmt(fac.totals.types_short)} type(s). Buy/Jobwork is your call — nothing is auto-applied.</div>}
              <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {/* PRESSABLE (2026-08-26). The blocking condition lives on ANOTHER SCREEN — M18/M19 in
                    T&A — and nothing on this calculator shows it, so a dead button here was a refusal
                    with its reason two modules away. commitPlan() names the exact milestones; it should
                    be allowed to. Contrast the two 'Pick a GPO first' buttons above, correctly left
                    disabled: their missing input is the picker directly beside them. */}
                <button style={{ ...btn, background: C.green, cursor: commitBusy ? 'wait' : 'pointer', opacity: commitBusy ? 0.6 : 1 }}
                  disabled={commitBusy} onClick={commitPlan}>{commitBusy ? 'Reserving…' : '🔒 Commit / Reserve this plan'}</button>
              </div>
            </>
          )}

          {/* Current factory commitments */}
          <div style={{ marginTop: 12, borderTop: `1px solid ${C.line}`, paddingTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowCommits(s => !s)}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>{showCommits ? '▾' : '▸'} Current factory commitments ({reservations.data.length} reservations)</div>
              <span onClick={(e) => { e.stopPropagation(); loadReservations(); }} style={{ fontSize: 11, color: C.ink, cursor: 'pointer' }}>↻ refresh</span>
            </div>
            {showCommits && (
              <div style={{ marginTop: 8 }}>
                {reservations.summary.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    {reservations.summary.map((s, i) => (
                      <span key={i} style={{ fontSize: 11, background: C.soft, borderRadius: 8, padding: '3px 9px' }}>
                        <strong>{s.machine_name || s.machine_code}</strong>: {fmt(s.committed)}/{fmt(s.total)} booked ({fmt(s.gpos)} GPO)
                      </span>
                    ))}
                  </div>
                )}
                {reservations.data.length === 0 ? <div style={{ fontSize: 12, color: '#aaa' }}>No active reservations.</div>
                  : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead><tr style={{ background: C.soft, textAlign: 'left' }}>
                      {['GPO', 'Machine', 'Qty', 'Window', 'By', ''].map(h => <th key={h} style={{ padding: '6px 8px', color: '#666', borderBottom: `1px solid ${C.line}` }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {reservations.data.map(r => (
                        <tr key={r.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                          <td style={{ padding: '6px 8px', fontWeight: 700, color: C.ink }}>{r.gpo_no}</td>
                          <td style={{ padding: '6px 8px' }}>{r.machine_name || r.machine_code}</td>
                          <td style={{ padding: '6px 8px' }}>{fmt(r.qty_reserved)}</td>
                          <td style={{ padding: '6px 8px' }}>{r.start_date} → {r.end_date}</td>
                          <td style={{ padding: '6px 8px', color: '#888' }}>{r.committed_by_name || '—'}</td>
                          <td style={{ padding: '6px 8px' }}><span onClick={() => releasePlan(r.gpo_no)} style={{ color: C.red, cursor: 'pointer', fontWeight: 600 }} title="Release this GPO's reservations">✕ release</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Per-operation breakdown + SAM source */}
      {result?.ops?.length > 0 && (
        <div style={box}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 8 }}>OPERATIONS · SAM SOURCE ({result.source || '—'})</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ background: C.soft, textAlign: 'left' }}>
              {['Operation', 'Op M/C', '→ Inventory', 'SAM (min)', 'Source', mode === 'standard' ? 'Std obs.' : '', 'Action'].map((h, i) =>
                h ? <th key={i} style={{ padding: '8px 10px', fontSize: 11, color: '#555', borderBottom: `2px solid ${C.line}` }}>{h}</th> : null)}
            </tr></thead>
            <tbody>
              {result.ops.map((o, i) => {
                const src = SAM_SRC[o.sam_source] || SAM_SRC.none;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f3f3' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{o.operation_name}</td>
                    <td style={{ padding: '8px 10px' }}>{o.machine_type || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>{o.unmapped ? <span style={{ color: C.amber, fontWeight: 700 }}>⚠ none</span> : `${o.mm_label} (${o.mm_machine_code})`}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700 }}>
                      {o.missing_sam
                        ? <input style={{ ...inp, width: 70, padding: '4px 6px' }} type="number" placeholder="?"
                            value={manual[o.process_code] ?? ''} onChange={e => setManual(m => ({ ...m, [o.process_code]: e.target.value }))} />
                        : fmt(o.sam, 3)}
                    </td>
                    <td style={{ padding: '8px 10px' }}><span style={{ padding: '2px 8px', borderRadius: 9, fontSize: 10, fontWeight: 700, background: src.bg, color: src.c }}>{src.label}</span></td>
                    {mode === 'standard' && <td style={{ padding: '8px 10px', color: '#888' }}>{o.observation_count > 0 ? `${o.observation_count} obs.` : '—'}</td>}
                    <td style={{ padding: '8px 10px' }}>
                      {/* Manual entry → seed standard; measured/confirmed → self-tune */}
                      {o.missing_sam && manual[o.process_code] > 0 &&
                        <button style={{ ...btn, padding: '4px 10px', fontSize: 11 }} onClick={() => recordSam(o, manual[o.process_code])}>Save → standard</button>}
                      {mode === 'actual' && o.sam_source === 'measured' && o.sam > 0 &&
                        <button style={{ ...btn, padding: '4px 10px', fontSize: 11, background: C.green }} onClick={() => recordSam(o, o.sam)}>Confirm → tune standard</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {result.ops.some(o => o.missing_sam) &&
            <div style={{ marginTop: 8, fontSize: 11, color: '#888' }}>Fill missing SAMs above, then <button style={{ ...btn, padding: '4px 10px', fontSize: 11 }} disabled={busy} onClick={() => calculate()}>Recalculate</button> — saving a manual SAM seeds/updates the self-tuning standard library.</div>}
        </div>
      )}
    </div>
  );
}

function Card({ title, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>{title}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: accent || '#1a1a2e', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
